import type { Models } from '~/services-v2/financial-bff/financial-bff'
import { useMutation } from '@tanstack/vue-query'
import queryClient, { queryKeys } from '~/queryClient'
import financialBff from '~/services-v2/financial-bff/financial-bff'

export type CommissioningRequestResponse = Models['CommissioningRequestResponse']
export type CommissioningMonitoringInput = Models['MonitoringInput']

export class CommissioningRequestError extends Error {
  readonly code: string

  constructor(code: string, message: string) {
    super(message)
    this.name = 'CommissioningRequestError'
    this.code = code
  }
}

export interface UseCommissioningRequestParams {
  totalPowerKw: number
  monitorings: Array<{
    brand: string
    expectedPlantName?: string
    inverters: string[]
  }>
}

export default function useCommissioningRequest(
  installationId: Ref<string | undefined>,
  _projectId: Ref<string | undefined>,
) {
  const mutationKey = queryKeys.INSTALLATION.COMMISSIONING_REQUEST(installationId.value as string)

  const mutationFn = async (params: UseCommissioningRequestParams) => {
    if (!installationId.value) {
      throw new Error('Installation ID is required')
    }

    const res = await financialBff('mutation')({
      installation_commissioning_request: [
        {
          input: {
            installation_id: installationId.value,
            total_power_kw: params.totalPowerKw,
            monitorings: params.monitorings.map(monitoring => ({
              brand: monitoring.brand,
              expected_plant_name: monitoring.expectedPlantName,
              inverters: monitoring.inverters,
            })),
          },
        },
        {
          success: true,
          message: true,
          data: {
            installation_id: true,
            code: true,
            monitorings: {
              id: true,
              brand: true,
              status: true,
              installed_inverters: {
                id: true,
                serial_number: true,
                status: true,
              },
            },
          },
        },
      ],
    }, {
      operationName: 'InstallationCommissioningRequest',
    })

    const result = res.installation_commissioning_request

    if (!result.success) {
      throw new CommissioningRequestError(
        result.data?.code ?? 'TECHNICAL_FAILURE',
        result.message,
      )
    }

    return result
  }

  const mutation = useMutation({
    mutationKey,
    mutationFn,
    retry: 0,
    onSuccess: () => {
      if (!installationId.value)
        return

      queryClient.invalidateQueries({
        queryKey: queryKeys.INSTALLATION.COMMISSIONING_STATUS(installationId.value),
      })
    },
  })

  return {
    ...mutation,
    mutateAsync: (params: UseCommissioningRequestParams) => mutation.mutateAsync(params),
  }
}
